import React, { useEffect, useMemo, useState } from "react";
import {
  alpha,
  Box,
  Card,
  CardContent,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import LinkIcon from "@mui/icons-material/Link";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context";
import { openAppConfirm } from "../../components/AppDialogs";
import Titles from "../titles";
import CatalogLibraryShell from "../titles/CatalogLibraryShell";
import {
  findTitleFilterKey,
  titleFilterKey,
  titleKeysMatch,
} from "../titles/catalogTitleFilter";
import SlideCardThumb from "../titles/components/SlideCardThumb";
import {
  GetLibraryCall,
  AddLibraryReferenceCall,
  RemoveLibraryReferenceCall,
  GetTitlesCallNoStore,
  AddSubTitlesCall,
  AddTitlesCall,
  GetLibraryVisibilityCall,
  UpdateLibraryVisibilityCall,
  ReorderLibraryReferencesCall,
} from "../../redux/api/title.api";

const ACCENT = "#0061FE";

const SLIDE_LIBRARY_CATEGORIES = new Set(["his", "emb", "rad"]);
const MODEL_LIBRARY_CATEGORIES = new Set(["three_d_model", "emb_3d_model", "anatomy"]);

function resolveLibraryCategory(item) {
  return String(item?.library_category || "his").toLowerCase();
}

function isInMediaMode(item, mediaMode) {
  const category = resolveLibraryCategory(item);
  if (category === "presentation") return false;
  if (mediaMode === "models") return MODEL_LIBRARY_CATEGORIES.has(category);
  return SLIDE_LIBRARY_CATEGORIES.has(category) || !item?.library_category;
}

function countSlidesInTitle(item) {
  return (item?.subtitles || []).reduce((sum, sub) => {
    const own = Array.isArray(sub?.slides) ? sub.slides.length : 0;
    const refs = Array.isArray(sub?.referencedSlides) ? sub.referencedSlides.length : 0;
    // When linked-only, referencedSlides carry the visible cards.
    return sum + (own > 0 ? own : refs);
  }, 0);
}

function getLocalizedTitle(entity) {
  const list = Array.isArray(entity?.titleLocalizations)
    ? entity.titleLocalizations.filter((e) => e?.field === "title")
    : [];
  return list?.[0]?.value || "";
}

const STUDENT_VIEW_OPTIONS = [
  {
    value: "private",
    labelKey: "pages.myLibrary.studentViewPrivate",
    hintKey: "pages.myLibrary.studentViewPrivateHint",
    icon: LockOutlinedIcon,
  },
  {
    value: "all",
    labelKey: "pages.myLibrary.studentViewPublic",
    hintKey: "pages.myLibrary.studentViewPublicHint",
    icon: PublicOutlinedIcon,
  },
  {
    value: "none",
    labelKey: "pages.myLibrary.studentViewHidden",
    hintKey: "pages.myLibrary.studentViewHiddenHint",
    icon: VisibilityOffOutlinedIcon,
  },
];

/** Compact "Students see: X" dropdown — a setting, not a gallery filter. */
function StudentViewMenu({ value, onChange, darkMode }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const current =
    STUDENT_VIEW_OPTIONS.find((o) => o.value === value) || STUDENT_VIEW_OPTIONS[2];
  const CurrentIcon = current.icon;

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        aria-label={t("pages.myLibrary.studentView")}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          height: 36,
          pl: 1.25,
          pr: 0.75,
          m: 0,
          border: "1px solid",
          borderColor: darkMode ? "rgba(255,255,255,0.12)" : alpha("#0f172a", 0.12),
          borderRadius: 2.5,
          bgcolor: "transparent",
          cursor: "pointer",
          font: "inherit",
          fontSize: 13.5,
          whiteSpace: "nowrap",
          color: theme.palette.text.secondary,
          "&:hover": { borderColor: alpha(ACCENT, 0.5) },
        }}
      >
        {t("pages.myLibrary.studentsSee")}
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          <CurrentIcon sx={{ fontSize: 16, color: ACCENT }} />
          {t(current.labelKey)}
        </Box>
        <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { mt: 0.75, width: 320, maxWidth: "calc(100vw - 32px)", p: 0.5 } }}
      >
        {STUDENT_VIEW_OPTIONS.map((option) => {
          const Icon = option.icon;
          const selected = option.value === value;
          return (
            <MenuItem
              key={option.value}
              selected={selected}
              onClick={() => {
                setAnchorEl(null);
                if (!selected) onChange(option.value);
              }}
              sx={{ alignItems: "flex-start", gap: 1.25, py: 1, borderRadius: 1.5, whiteSpace: "normal" }}
            >
              <Icon sx={{ fontSize: 18, mt: 0.25, color: selected ? ACCENT : "text.secondary" }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{t(option.labelKey)}</Typography>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.4 }}>
                  {t(option.hintKey).replace(/^[^—]*—\s*/, "")}
                </Typography>
              </Box>
              {selected ? <CheckRoundedIcon sx={{ fontSize: 18, color: ACCENT, mt: 0.25 }} /> : null}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

function HowItWorksButton({ darkMode }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const helpCardBg = darkMode ? "rgba(28, 30, 34, 0.98)" : "#fff";

  return (
    <Tooltip
      arrow
      placement="bottom-start"
      enterTouchDelay={0}
      title={
        <Box sx={{ maxWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: "inherit" }}>
            {t("pages.myLibrary.howItWorksTitle")}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mb: 1, lineHeight: 1.45, color: "inherit" }}>
            {t("pages.myLibrary.howItWorksIntro")}
          </Typography>
          {STUDENT_VIEW_OPTIONS.map((option) => (
            <Typography
              key={option.value}
              variant="caption"
              sx={{ display: "block", lineHeight: 1.45, mb: 0.4, color: "inherit" }}
            >
              {t(option.hintKey)}
            </Typography>
          ))}
        </Box>
      }
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 340,
            bgcolor: helpCardBg,
            color: theme.palette.text.primary,
            boxShadow: "0 16px 40px rgba(16,24,40,0.18)",
            borderRadius: 2,
            p: 1.5,
            border: "1px solid",
            borderColor: darkMode ? "rgba(255,255,255,0.08)" : alpha("#0f172a", 0.08),
          },
        },
        arrow: { sx: { color: helpCardBg } },
      }}
    >
      <Box
        component="button"
        type="button"
        aria-label={t("pages.myLibrary.howItWorksAria")}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 30,
          height: 30,
          border: 0,
          borderRadius: "50%",
          bgcolor: "transparent",
          color: theme.palette.text.secondary,
          p: 0,
          m: 0,
          cursor: "pointer",
          "&:hover": { color: ACCENT, bgcolor: alpha(ACCENT, darkMode ? 0.16 : 0.08) },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 20 }} />
      </Box>
    </Tooltip>
  );
}

function MyLibraryPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const navigate = useNavigate();
  const authId = useSelector((state) => state?.auth?.id);
  const lang = useSelector((state) => state?.general?.lang) || "en";
  const [mediaMode, setMediaMode] = useState("slides"); // "slides" | "models"
  const [selectedCategoryKey, setSelectedCategoryKey] = useState(null);
  const [catalogSortBy, setCatalogSortBy] = useState("newest");
  const [catalogViewMode, setCatalogViewMode] = useState("grid");
  // Which public catalogue to browse when adding from library
  const browseCategory = mediaMode === "models" ? "three_d_model" : "his";
  // Student-view visibility key for the active media mode
  const visibilityCategory = mediaMode === "models" ? "three_d_model" : "his";

  const [libraryData, setLibraryData] = useState({ private: [], referenced: [] });
  const [browseOpen, setBrowseOpen] = useState(false);
  const [publicTitlesForTab, setPublicTitlesForTab] = useState([]);
  const [loadingPublic, setLoadingPublic] = useState(false);
  const [message, setMessage] = useState("");
  const [messageIsError, setMessageIsError] = useState(false);
  const [selectedSlides, setSelectedSlides] = useState([]);
  const [expandedTitleIds, setExpandedTitleIds] = useState({});
  const [targetSubtitleId, setTargetSubtitleId] = useState(null);
  const [targetTitleId, setTargetTitleId] = useState(null);
  const [browseSearchQuery, setBrowseSearchQuery] = useState("");
  const [visibilityByCategory, setVisibilityByCategory] = useState({});
  const [librarySearchQuery, setLibrarySearchQuery] = useState("");

  const loadLibrary = async () => {
    const res = await GetLibraryCall(lang);
    setLibraryData({
      private: res?.private || [],
      referenced: res?.referenced || [],
    });
  };

  const loadVisibility = async () => {
    const vis = await GetLibraryVisibilityCall();
    const raw = vis || {};
    // Backend uses "published" for "only my private slides"; frontend dropdown uses "private"
    const mapBackendToFrontend = (v) => (v === "published" ? "private" : v);
    setVisibilityByCategory({
      his: mapBackendToFrontend(raw.his),
      emb: mapBackendToFrontend(raw.emb),
      rad: mapBackendToFrontend(raw.rad),
      three_d_model: mapBackendToFrontend(raw.three_d_model),
    });
  };

  useEffect(() => {
    if (!authId) return;
    loadLibrary();
    loadVisibility();
  }, [authId, lang]);

  const privateForMode = useMemo(
    () => (libraryData.private || []).filter((i) => isInMediaMode(i, mediaMode)),
    [libraryData.private, mediaMode]
  );

  const libraryCategories = useMemo(() => {
    const order = [];
    const labels = new Map();
    const idsByKey = new Map();
    privateForMode.forEach((item) => {
      const label = getLocalizedTitle(item) || t("pages.myLibrary.untitled");
      const key = titleFilterKey(label) || item.id;
      const matched = findTitleFilterKey(order, key) || (order.includes(key) ? key : null);
      const finalKey = matched || key;
      if (!matched) {
        order.push(finalKey);
        labels.set(finalKey, label);
        idsByKey.set(finalKey, new Set([item.id]));
      } else {
        idsByKey.get(finalKey)?.add(item.id);
      }
    });
    return order.map((key) => ({
      key,
      label: labels.get(key) || key,
      count: privateForMode
        .filter((item) => idsByKey.get(key)?.has(item.id))
        .reduce((sum, item) => sum + countSlidesInTitle(item), 0),
      titleIds: Array.from(idsByKey.get(key) || []),
    }));
  }, [privateForMode, t]);

  const privateVisible = useMemo(() => {
    let list = privateForMode;
    if (selectedCategoryKey) {
      const cat = libraryCategories.find((c) => c.key === selectedCategoryKey);
      const idSet = new Set(cat?.titleIds || []);
      list = list.filter((item) => {
        if (idSet.has(item.id)) return true;
        const label = getLocalizedTitle(item);
        return titleKeysMatch(titleFilterKey(label), selectedCategoryKey);
      });
    }
    const sorted = [...list];
    sorted.sort((a, b) => {
      const aTitle = getLocalizedTitle(a) || "";
      const bTitle = getLocalizedTitle(b) || "";
      const aTime = new Date(a?.created_at || a?.updated_at || 0).getTime();
      const bTime = new Date(b?.created_at || b?.updated_at || 0).getTime();
      if (catalogSortBy === "az") return aTitle.localeCompare(bTitle);
      if (catalogSortBy === "za") return bTitle.localeCompare(aTitle);
      if (catalogSortBy === "oldest") return aTime - bTime;
      return bTime - aTime;
    });
    return sorted;
  }, [privateForMode, selectedCategoryKey, libraryCategories, catalogSortBy]);

  const allSlideCount = useMemo(
    () => privateForMode.reduce((sum, item) => sum + countSlidesInTitle(item), 0),
    [privateForMode]
  );

  const referencedForTab = (libraryData.referenced || []).filter((i) => {
    const type = (i.type || "").toString().toLowerCase();
    if (mediaMode === "models") {
      return MODEL_LIBRARY_CATEGORIES.has(type);
    }
    return SLIDE_LIBRARY_CATEGORIES.has(type);
  });

  const handleAddSection = () => {
    if (!authId) return;
    AddTitlesCall({
      lang,
      type: authId,
      title: "New Section",
      library_category: mediaMode === "models" ? "three_d_model" : "his",
    }).then(() => loadLibrary());
  };

  const handleMediaModeChange = (next) => {
    const mode = next === "models" ? "models" : "slides";
    if (mode === mediaMode) return;
    setMediaMode(mode);
    setSelectedCategoryKey(null);
    setLibrarySearchQuery("");
  };

  // Map of public_title_id -> array of slide_ids that are actually linked
  // into any private subtitle (via subtitle.referencedSlides).
  const linkedSlideIdsByPublicTitle = useMemo(() => {
    const map = {};
    (libraryData.private || []).forEach((pt) => {
      (pt.subtitles || []).forEach((sub) => {
        (sub.referencedSlides || []).forEach((ref) => {
          const publicTitleId = ref?.public_title_id;
          const slideId = ref?.id;
          if (!publicTitleId || !slideId) return;
          if (!map[publicTitleId]) map[publicTitleId] = new Set();
          map[publicTitleId].add(slideId);
        });
      });
    });
    const result = {};
    Object.keys(map).forEach((titleId) => {
      result[titleId] = Array.from(map[titleId]);
    });
    return result;
  }, [libraryData.private]);

  const handleAddReference = async (titleId) => {
    const res = await AddLibraryReferenceCall(titleId);
    if (res?.status === 201) {
      setMessageIsError(false);
      setMessage(t("pages.myLibrary.addedToLibrary"));
      await loadLibrary();
      setBrowseOpen(false);
    } else {
      setMessageIsError(true);
      setMessage(res?.message || t("pages.myLibrary.couldNotAdd"));
    }
  };

  const handleRemoveReference = async (titleId) => {
    const ok = await openAppConfirm({
      title: t("pages.myLibrary.removeFromLibraryConfirm"),
      message: t("pages.myLibrary.removeFromLibraryConfirm"),
      confirmText: "Remove",
      confirmColor: "error",
    });
    if (!ok) return;
    const res = await RemoveLibraryReferenceCall(titleId);
    if (res?.status === 200) {
      setMessageIsError(false);
      setMessage(t("pages.myLibrary.removedFromLibrary"));
      await loadLibrary();
    } else {
      setMessageIsError(true);
      setMessage(res?.message || t("pages.myLibrary.couldNotRemove"));
    }
  };

  const handleRemoveInlineRef = async (publicTitleId, slideId, targetSubtitleId) => {
    const res = await RemoveLibraryReferenceCall(publicTitleId, slideId, targetSubtitleId);
    if (res?.status === 200) {
      setMessageIsError(false);
      setMessage(t("pages.myLibrary.removedFromLibrary"));
      await loadLibrary();
    } else {
      setMessageIsError(true);
      setMessage(res?.message || t("pages.myLibrary.couldNotRemove"));
    }
  };

  const openAddFromLibrary = async (_subtitleId, item) => {
    // Link slides as separate subtitles under this private title.
    setTargetTitleId(item?.id || null);
    setTargetSubtitleId(null);
    setBrowseOpen(true);
    setSelectedSlides([]);
    setExpandedTitleIds({});
    setLoadingPublic(true);
    GetTitlesCallNoStore(lang, browseCategory).then((list) => {
      setPublicTitlesForTab(Array.isArray(list) ? list : []);
      setLoadingPublic(false);
    });
  };

  const currentVisibility = visibilityByCategory[visibilityCategory] || "none";

  const handleVisibilityChange = async (value) => {
    setVisibilityByCategory((prev) => ({
      ...prev,
      [visibilityCategory]: value,
    }));
    await UpdateLibraryVisibilityCall({
      category: visibilityCategory,
      visibility: value,
    });
  };

  const getTitleLabel = (item) => {
    const locs = item?.titleLocalizations || [];
    const t = locs.find((l) => l.field === "title");
    return t?.value || item?.id || "Untitled";
  };

  const getSubtitleLabel = (subtitle) => {
    const locs = subtitle?.titleLocalizations || [];
    const t = locs.find((l) => l.field === "title");
    return t?.value || subtitle?.id || "Untitled";
  };

  const getSlideLabel = (slide, idx, subtitle = null) => {
    const locs = slide?.slideLocalizations || slide?.slide_localizations || [];
    const t = locs.find((l) => l.field === "title");
    if (t?.value && t.value.trim()) return t.value.trim();
    if (subtitle) return getSubtitleLabel(subtitle);
    return `Slide ${(idx ?? 0) + 1}`;
  };


  // Only slides that already appear as cards in this collection are locked.
  // A stale or floating public-library link must not block adding the slide again.
  const isSlideInLibrary = (_item, slide) => {
    if (!targetTitleId || !slide?.id) return false;
    const folder = (libraryData.private || []).find(
      (entry) => String(entry?.id) === String(targetTitleId)
    );
    if (!folder) return false;
    return (folder.subtitles || []).some((sub) =>
      (sub.referencedSlides || []).some((ref) => String(ref?.id) === String(slide.id))
    );
  };

  const isSlideSelected = (titleId, slideId) =>
    selectedSlides.some((s) => s.titleId === titleId && s.slideId === slideId);

  const toggleSlideSelection = (titleId, slideId) => {
    setSelectedSlides((prev) => {
      const has = prev.some((s) => s.titleId === titleId && s.slideId === slideId);
      if (has) return prev.filter((s) => s.titleId !== titleId || s.slideId !== slideId);
      return [...prev, { titleId, slideId }];
    });
  };

  const toggleTitleExpanded = (titleId) => {
    setExpandedTitleIds((prev) => ({ ...prev, [titleId]: !prev[titleId] }));
  };

  const q = (browseSearchQuery || "").trim().toLowerCase();
  const filteredPublicTitlesForBrowse = useMemo(() => {
    if (!q) return publicTitlesForTab;
    return publicTitlesForTab
      .map((title) => {
        const titleLabel = getTitleLabel(title);
        const titleMatches = titleLabel.toLowerCase().includes(q);
        if (titleMatches) return title;
        const subtitles = (title.subtitles || [])
          .map((sub) => {
            const subLabel = getSubtitleLabel(sub);
            const subMatches = subLabel.toLowerCase().includes(q);
            const slides = (sub.slides || []).filter((slide, idx) => {
              const slideLabel = getSlideLabel(slide, idx, sub);
              return slideLabel.toLowerCase().includes(q);
            });
            const subHasMatch = subMatches || slides.length > 0;
            return subHasMatch ? { ...sub, slides: subMatches ? (sub.slides || []) : slides } : null;
          })
          .filter(Boolean);
        return subtitles.length > 0 ? { ...title, subtitles } : null;
      })
      .filter(Boolean);
  }, [publicTitlesForTab, browseSearchQuery]);

  const handleAddSelectedReferences = async () => {
    let added = 0;
    let firstError = "";
    for (const { titleId, slideId } of selectedSlides) {
      const res = await AddLibraryReferenceCall(
        titleId,
        slideId,
        targetSubtitleId || undefined,
        targetTitleId || undefined,
        lang
      );
      const status = Number(res?.status);
      if (status >= 200 && status < 300) {
        added += 1;
      } else if (!firstError) {
        firstError = res?.message || t("pages.myLibrary.couldNotAdd");
      }
    }
    await loadLibrary();
    setMessageIsError(!added);
    setMessage(
      added
        ? t("pages.myLibrary.addedSlidesToLibrary", { count: added })
        : firstError || t("pages.myLibrary.nothingToAdd")
    );
    setSelectedSlides([]);
    setTargetSubtitleId(null);
    setTargetTitleId(null);
    setBrowseOpen(false);
  };

  const openReferencedTitle = (item) => {
    const t = item?.type;
    const firstSlide = item?.subtitles?.[0]?.slides?.[0] || item?.slides?.[0];
    const slideId = firstSlide?.id;
    if (!slideId) return;
    if (t === "three_d_model") {
      navigate(`/showThreeD/${slideId}/${lang}`);
    } else {
      navigate(`/showTitle/${slideId}/${lang}`);
    }
  };

  if (!authId) {
    return null;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        pt={0}
        pb={0}
        sx={{
          // Match StorageLicenseBar: full width inside DashboardLayout px gutter.
          // Top spacing comes from StorageLicenseBar mb: 3 (= layout side padding).
          position: "relative",
          width: "100%",
          mx: 0,
          mt: 0,
          minHeight: "100%",
          bgcolor: "transparent",
        }}
      >
        {message && (
          <Typography
            variant="body2"
            color={messageIsError ? "error.main" : "success.main"}
            sx={{ mb: 1.5 }}
          >
            {message}
          </Typography>
        )}

        <CatalogLibraryShell
          title={t("pages.myLibrary.title")}
          subtitle={t("pages.myLibrary.librarySubheading")}
          darkMode={darkMode}
          segment={mediaMode}
          onSegmentChange={handleMediaModeChange}
          categories={libraryCategories}
          selectedCategoryKey={selectedCategoryKey}
          onCategoryChange={setSelectedCategoryKey}
          allLabel={
            mediaMode === "models"
              ? t("pages.myLibrary.allModels")
              : t("pages.myLibrary.allSlides")
          }
          allCount={allSlideCount}
          searchQuery={librarySearchQuery}
          onSearchChange={setLibrarySearchQuery}
          searchPlaceholder={
            mediaMode === "models"
              ? "Search models, titles or description..."
              : "Search slides, titles or description..."
          }
          sortBy={catalogSortBy}
          onSortChange={setCatalogSortBy}
          viewMode={catalogViewMode}
          onViewModeChange={setCatalogViewMode}
          categoriesLabel={t("pages.myLibrary.sections")}
          primaryActionLabel={t("pages.myLibrary.newSection")}
          onPrimaryAction={handleAddSection}
          titleAdornment={<HowItWorksButton darkMode={darkMode} />}
          galleryHeaderExtra={
            <StudentViewMenu
              value={currentVisibility}
              onChange={handleVisibilityChange}
              darkMode={darkMode}
            />
          }
        >
          <Titles
            type={authId}
            readOnly={false}
            library_category={null}
            embedded
            hideToolbar
            viewMode={catalogViewMode}
            searchQuery={librarySearchQuery}
            onSearchQueryChange={setLibrarySearchQuery}
            itemsFromLibrary={privateVisible}
            onRemoveLibraryRef={handleRemoveInlineRef}
            onLibraryRefresh={loadLibrary}
            onReorderLibraryRefs={async (targetSubtitleId, orderedSlideIds) => {
              const res = await ReorderLibraryReferencesCall(
                targetSubtitleId,
                orderedSlideIds
              );
              await loadLibrary();
              if (res?.status != null && res.status !== 200) {
                setMessageIsError(true);
                setMessage(res?.message || "Could not save slide order.");
              }
            }}
            addFromLibraryNode={(item) => (
              <MDButton
                variant="text"
                color="info"
                startIcon={<LinkIcon sx={{ fontSize: 18 }} />}
                onClick={() => openAddFromLibrary(item.subtitles?.[0]?.id, item)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 1.25,
                  height: 34,
                  minHeight: 34,
                  color: `${ACCENT} !important`,
                  bgcolor: "transparent !important",
                  "&:hover": { bgcolor: `${alpha(ACCENT, 0.08)} !important` },
                }}
              >
                {t("pages.myLibrary.addFromLibrary")}
              </MDButton>
            )}
          />
        </CatalogLibraryShell>
      </MDBox>

      <Dialog
        open={browseOpen}
        onClose={() => {
          setBrowseOpen(false);
          setBrowseSearchQuery("");
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t("pages.myLibrary.addFromLibrary")} —{" "}
          {mediaMode === "models" ? t("pages.myLibrary.models3d") : t("pages.myLibrary.histology")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select slides to add to your library. Expand each title to see subtitles and slides, then check the slides you want. Files are linked, not copied.
          </Typography>
          {!loadingPublic && publicTitlesForTab.length > 0 && (
            <TextField
              fullWidth
              size="small"
              placeholder="Search titles, subtitles, slides…"
              value={browseSearchQuery}
              onChange={(e) => setBrowseSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ "aria-label": "Search public library" }}
            />
          )}
          {loadingPublic && <Typography variant="body2">Loading…</Typography>}
          {!loadingPublic && publicTitlesForTab.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {`No public ${
                mediaMode === "models"
                  ? t("pages.myLibrary.models3d").toLowerCase()
                  : "slide"
              } titles available.`}
            </Typography>
          )}
          {!loadingPublic && publicTitlesForTab.length > 0 && filteredPublicTitlesForBrowse.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No results for &quot;{browseSearchQuery.trim()}&quot;.
            </Typography>
          )}
          {!loadingPublic &&
            filteredPublicTitlesForBrowse.map((item) => {
              const expanded = !!expandedTitleIds[item.id];
              const subtitles = item?.subtitles || [];
              return (
                <Card key={item.id} variant="outlined" sx={{ mb: 1 }}>
                  <CardContent sx={{ py: 0, px: 1.5, "&:last-child": { pb: 1 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: 48,
                        gap: 0.5,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => toggleTitleExpanded(item.id)}
                        sx={{ p: 0.5 }}
                        aria-label={expanded ? "Collapse" : "Expand"}
                      >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                        {getTitleLabel(item)}
                      </Typography>
                    </Box>
                    <Collapse in={expanded} timeout="auto">
                      <Box sx={{ pl: 2, pr: 1, pb: 1.5 }}>
                        {subtitles.length === 0 && (
                          <Typography variant="caption" color="text.secondary">
                            No subtitles
                          </Typography>
                        )}
                        {subtitles.map((sub) => {
                          const slides = sub?.slides || [];
                          return (
                            <Box key={sub.id} sx={{ mb: 2 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 1 }}>
                                {getSubtitleLabel(sub)}
                              </Typography>
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
                                  gap: 1.5,
                                }}
                              >
                                {slides.length === 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    No slides
                                  </Typography>
                                )}
                                {slides.map((slide, slideIdx) => {
                                  const label = getSlideLabel(slide, slideIdx, sub);
                                  const subLabel = getSubtitleLabel(sub);
                                  const showLabel = label && label !== subLabel;
                                  const isDark = theme.palette.mode === "dark";
                                  const inLibrary = isSlideInLibrary(item, slide);
                                  const checked = inLibrary || isSlideSelected(item.id, slide.id);
                                  return (
                                    <Box
                                      key={slide.id}
                                      sx={{
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                                        border: "1px solid",
                                        borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                                        boxShadow: 1,
                                        position: "relative",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          position: "absolute",
                                          top: 4,
                                          left: 4,
                                          zIndex: 1,
                                          bgcolor: theme.palette.mode === "dark" ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.95)",
                                          borderRadius: 1,
                                        }}
                                      >
                                        <Checkbox
                                          size="small"
                                          checked={checked}
                                          disabled={inLibrary}
                                          onChange={() => !inLibrary && toggleSlideSelection(item.id, slide.id)}
                                          sx={{ p: 0.25 }}
                                        />
                                      </Box>
                                      <Box sx={{ position: "relative", width: "100%", paddingTop: "75%" }}>
                                        <Box sx={{ position: "absolute", inset: 0 }}>
                                          <SlideCardThumb
                                            slide={slide}
                                            alt={label || subLabel}
                                            height="100%"
                                            objectPosition="center"
                                            dark={isDark}
                                          />
                                        </Box>
                                      </Box>
                                      {showLabel && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            px: 0.75,
                                            py: 0.5,
                                            fontWeight: 500,
                                            lineHeight: 1.3,
                                            wordBreak: "break-word",
                                            minHeight: "2.6em",
                                          }}
                                        >
                                          {label}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              );
            })}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <MDButton variant="text" onClick={() => setBrowseOpen(false)}>
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="primary"
            onClick={handleAddSelectedReferences}
            disabled={selectedSlides.length === 0}
          >
            Add selected ({selectedSlides.length})
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default MyLibraryPage;
